import { useState } from "react";
import { useLang } from "../i18n";
import { Icon } from "./Icon";
import { MUNICIPALITIES } from "../data/municipalities";
import { SHELTERS } from "../data/shelters";
import { COASTLINE_RINGS, COASTAL_TOWNS } from "../data/prCoastline";

const KM_PER_MI = 1.60934;

function havKm(a, b) {
  const dLat = ((a[0] - b[0]) * Math.PI) / 180;
  const dLng = ((a[1] - b[1]) * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos((b[0] * Math.PI) / 180) * Math.cos((a[0] * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
}

// Distancia punto-segmento (proyección equirectangular local, suficiente a esta escala); devuelve también el punto proyectado
function segKm(p, a, b) {
  const kx = Math.cos((p[0] * Math.PI) / 180) * 111.32;
  const ky = 110.574;
  const px = p[1] * kx, py = p[0] * ky;
  const ax = a[1] * kx, ay = a[0] * ky;
  const bx = b[1] * kx, by = b[0] * ky;
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx, cy = ay + t * dy;
  return { d: Math.hypot(px - cx, py - cy), point: [cy / ky, cx / kx] };
}

// Punto de costa más cercano (y distancia)
function nearestCoast(lat, lng) {
  const p = [lat, lng];
  let min = Infinity;
  let point = null;
  for (const ring of COASTLINE_RINGS) {
    for (let i = 0; i < ring.length - 1; i++) {
      const { d, point: cp } = segKm(p, ring[i], ring[i + 1]);
      if (d < min) { min = d; point = cp; }
    }
  }
  return { km: min, point };
}

// Distancia de cada refugio a la costa, precalculada una sola vez
const SHELTER_COAST = new Map();
for (const [town, list] of Object.entries(SHELTERS)) {
  for (const s of list) {
    if (s.lat != null && s.lng != null) SHELTER_COAST.set(s, { town, coastKm: nearestCoast(s.lat, s.lng).km });
  }
}

function nearestInlandShelter(lat, lng) {
  let best = null;
  let bestD = Infinity;
  for (const [s, meta] of SHELTER_COAST) {
    if (meta.coastKm < 1.6) continue; // solo puntos seguros tierra adentro
    const d = havKm([lat, lng], [s.lat, s.lng]);
    if (d < bestD) { bestD = d; best = { ...s, town: meta.town, distKm: d }; }
  }
  return best;
}

function bearingDeg(from, to) {
  const f1 = (from[0] * Math.PI) / 180, f2 = (to[0] * Math.PI) / 180;
  const dLng = ((to[1] - from[1]) * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(f2);
  const x = Math.cos(f1) * Math.sin(f2) - Math.sin(f1) * Math.cos(f2) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

const DIRS = {
  es: ["norte", "noreste", "este", "sureste", "sur", "suroeste", "oeste", "noroeste"],
  en: ["north", "northeast", "east", "southeast", "south", "southwest", "west", "northwest"],
};

function nearestTown(lat, lng) {
  let best = null;
  let bestD = Infinity;
  for (const m of MUNICIPALITIES) {
    const d = havKm([lat, lng], [m.lat, m.lng]);
    if (d < bestD) { bestD = d; best = m; }
  }
  return best;
}

export function TsunamiZone() {
  const { lang } = useLang();
  const [state, setState] = useState("idle"); // idle | locating | done | error
  const [result, setResult] = useState(null);

  const check = () => {
    if (!navigator.geolocation) { setState("error"); return; }
    setState("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const { km, point } = nearestCoast(lat, lng);
        const mi = km / KM_PER_MI;
        const town = nearestTown(lat, lng);
        const coastal = town ? COASTAL_TOWNS.has(town.name) : false;
        let level = "safe";
        if (coastal && mi <= 1) level = "zone";
        else if (coastal && mi <= 2.5) level = "near";
        // Ruta de escape: dirección hacia el punto seguro tierra adentro (evita ambigüedad cuando hay agua a ambos lados)
        let escape = null;
        if (level !== "safe" && point) {
          const shelter = nearestInlandShelter(lat, lng);
          const target = shelter ? [shelter.lat, shelter.lng] : [lat, lng];
          const bearing = bearingDeg(point, target);
          escape = { bearing, shelter };
        }
        setResult({ km, mi, town, coastal, level, escape });
        setState("done");
      },
      () => setState("error"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const waRouteText = (r) => {
    if (!r?.escape) return "";
    const dir = DIRS[lang][Math.round(r.escape.bearing / 45) % 8];
    const sh = r.escape.shelter;
    return lang === "es"
      ? `RUTA DE ESCAPE TSUNAMI — ${r.town?.name || "PR"}\n• Dirección: hacia el ${dir.toUpperCase()} (tierra adentro)\n• Distancia a la costa: ${r.mi.toFixed(1)} millas\n${sh ? `• Punto seguro cercano: ${sh.facility} (${sh.town}), a ${(sh.distKm / KM_PER_MI).toFixed(1)} millas tierra adentro\n` : ""}\nVía preparatepr.org — Puerto Rico, Preparado Siempre`
      : `TSUNAMI ESCAPE ROUTE — ${r.town?.name || "PR"}\n• Direction: ${dir.toUpperCase()} (inland)\n• Distance to coast: ${r.mi.toFixed(1)} miles\n${sh ? `• Nearby safe point: ${sh.facility} (${sh.town}), ${(sh.distKm / KM_PER_MI).toFixed(1)} miles inland\n` : ""}\nVia preparatepr.org — Puerto Rico, Always Prepared`;
  };

  const VERDICTS = {
    zone: {
      cls: "border-red-700 bg-red-50",
      icon: "TriangleAlert",
      iconCls: "text-red-700",
      title: { es: "Estás en una zona probable de evacuación por tsunami", en: "You are in a probable tsunami evacuation zone" },
      body: {
        es: "Ante un sismo fuerte o aviso de tsunami: camina YA a terreno alto (100+ pies de elevación o 1 milla tierra adentro) o sube a un edificio de hormigón de 3+ pisos.",
        en: "On a strong quake or tsunami alert: walk NOW to high ground (100+ ft elevation or 1 mile inland) or go up a concrete building 3+ stories.",
      },
    },
    near: {
      cls: "border-amber-600 bg-amber-50",
      icon: "Waves",
      iconCls: "text-amber-700",
      title: { es: "Estás cerca de la zona costera de tsunami", en: "You are near the coastal tsunami zone" },
      body: {
        es: "Conoce tu ruta a terreno alto y el punto de encuentro de tu familia. Las olas pueden subir ríos y quebradas más allá de la playa.",
        en: "Know your route to high ground and your family's meeting point. Waves can run up rivers and streams beyond the beach.",
      },
    },
    safe: {
      cls: "border-green-700 bg-green-50",
      icon: "CheckCircle2",
      iconCls: "text-green-700",
      title: { es: "Fuera de la zona probable de tsunami", en: "Outside the probable tsunami zone" },
      body: {
        es: "Tu ubicación está a suficiente distancia de la costa. Aun así, ten tu plan familiar listo: los tsunamis no dan mucho aviso en la costa.",
        en: "Your location is far enough from the coast. Still, keep your family plan ready: tsunamis give little warning on the coast.",
      },
    },
  };

  const v = result ? VERDICTS[result.level] : null;

  return (
    <section data-testid="tsunami-zone" className="mt-4 rounded-lg border-2 border-[#1C2B4A] bg-white p-6 sm:p-8">
      <h3 className="flex items-center gap-2 font-display text-xl sm:text-2xl font-bold text-slate-900">
        <Icon name="Waves" className="w-6 h-6 text-[#1C2B4A]" />
        {lang === "es" ? "¿Estoy en una zona de tsunami?" : "Am I in a tsunami zone?"}
      </h3>
      <p className="mt-2 text-sm sm:text-base text-slate-600">
        {lang === "es"
          ? "Usa el GPS de tu teléfono para estimar tu distancia a la costa y saber si estás en una zona probable de evacuación. Estimación orientativa — confirma tu zona exacta en los mapas oficiales de la Red Sísmica de PR o NMEAD."
          : "Use your phone's GPS to estimate your distance from the coast and whether you're in a probable evacuation zone. Rough estimate — confirm your exact zone on the official PR Seismic Network or NMEAD maps."}
      </p>
      <button
        data-testid="tsunami-zone-btn"
        onClick={check}
        disabled={state === "locating"}
        className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#1C2B4A] px-5 py-2.5 text-sm font-bold text-white hover:bg-amber-700 transition-colors duration-200 active:scale-[0.98] disabled:opacity-60"
      >
        <Icon name="MapPin" className="w-4 h-4" />
        {state === "locating"
          ? (lang === "es" ? "Ubicando…" : "Locating…")
          : (lang === "es" ? "Verificar mi ubicación" : "Check my location")}
      </button>

      {state === "error" && (
        <p data-testid="tsunami-zone-error" className="mt-4 rounded-md border border-red-300 bg-red-50 p-4 text-sm font-semibold text-red-800">
          {lang === "es"
            ? "No pudimos acceder a tu ubicación. Permite el GPS del navegador e inténtalo de nuevo."
            : "We couldn't access your location. Allow browser GPS and try again."}
        </p>
      )}

      {state === "done" && result && v && (
        <div data-testid="tsunami-zone-result" className={`mt-4 rounded-md border-l-4 border p-5 ${v.cls}`}>
          <p className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
            <Icon name={v.icon} className={`w-6 h-6 ${v.iconCls}`} />
            {v.title[lang]}
          </p>
          <p className="mt-2 text-sm sm:text-base text-slate-700">{v.body[lang]}</p>
          <p className="mt-3 text-xs text-slate-500">
            {lang === "es" ? "Distancia estimada a la costa" : "Estimated distance to coast"}: <strong>{result.mi.toFixed(1)} mi</strong>
            {result.town && <> · {lang === "es" ? "Municipio más cercano" : "Nearest municipality"}: <strong>{result.town.name}</strong>{result.coastal ? (lang === "es" ? " (costero)" : " (coastal)") : ""}</>}
          </p>

          {result.escape && (
            <div data-testid="escape-route" className="mt-4 rounded-md border-2 border-dashed border-slate-400 bg-white p-4">
              <p className="flex items-center gap-2 font-display text-base font-bold text-slate-900">
                <Icon name="Route" className="w-5 h-5 text-[#1C2B4A]" />
                {lang === "es" ? "Ruta de escape" : "Escape route"}
              </p>
              <p data-testid="escape-direction" className="mt-2 flex items-center gap-2 text-sm sm:text-base text-slate-700">
                <Icon name="Navigation" className="w-5 h-5 text-red-700 shrink-0" style={{ transform: `rotate(${result.escape.bearing}deg)` }} />
                {lang === "es" ? (
                  <>Muévete hacia el <strong className="uppercase">{DIRS.es[Math.round(result.escape.bearing / 45) % 8]}</strong> (tierra adentro, alejándote de la costa)</>
                ) : (
                  <>Head <strong className="uppercase">{DIRS.en[Math.round(result.escape.bearing / 45) % 8]}</strong> (inland, away from the coast)</>
                )}
              </p>
              {result.escape.shelter && (
                <p data-testid="escape-shelter" className="mt-2 text-sm sm:text-base text-slate-700">
                  {lang === "es" ? "Punto seguro cercano" : "Nearby safe point"}: <strong>{result.escape.shelter.facility}</strong> ({result.escape.shelter.town})
                  {" — "}{(result.escape.shelter.distKm / KM_PER_MI).toFixed(1)} {lang === "es" ? "millas tierra adentro" : "miles inland"}
                  <a
                    data-testid="escape-shelter-maps"
                    href={`https://www.google.com/maps/search/?api=1&query=${result.escape.shelter.lat},${result.escape.shelter.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2 inline-flex items-center gap-1 font-bold text-amber-800 underline hover:text-amber-900"
                  >
                    <Icon name="MapPin" className="w-3.5 h-3.5" /> Google Maps
                  </a>
                </p>
              )}
              <a
                data-testid="escape-share-wa"
                href={`https://wa.me/?text=${encodeURIComponent(waRouteText(result))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 rounded-md border-2 border-green-700 px-3 py-1.5 text-xs font-bold text-green-700 hover:bg-green-700 hover:text-white transition-colors duration-150 active:scale-[0.97]"
              >
                <Icon name="MessageCircle" className="w-3.5 h-3.5" /> WhatsApp
              </a>
              <p className="mt-2 text-[11px] text-slate-400">
                {lang === "es"
                  ? "Esta tarjeta queda disponible sin conexión una vez cargada la página."
                  : "This card stays available offline once the page is loaded."}
              </p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
